import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { auth, db } from '../firebase';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { updateEmail, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { Logo } from '../components/Logo';

function Yggdrasil() {
  const navigate = useNavigate();
  const { currentUser, userData, logout } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('filtering');
  const [testConnection, setTestConnection] = useState(false);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [savingBarcode, setSavingBarcode] = useState(false);
  
  // Password change state
  const [passwordChange, setPasswordChange] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    showForm: false,
    loading: false
  });

  const locations = [
    'Non-Fiction', 'Fiction', 'MS Fiction', 'MS Non-Fiction',
    'MS Quick', 'Graphic Novels', 'MS Graphic Novels', 'MS GN Non-Fiction',
    'Easy', 'Early Non-Fiction', 'Reference', 'Spanish'
  ];

  const contentFilterOptions = [
    {
      key: 'flagOverGrade8',
      label: 'Over Grade 8 Content',
      description: 'Flag books intended for high school (9-12) and adult audiences',
      color: 'text-red-600',
      recommended: true
    },
    {
      key: 'flagSexualContent',
      label: 'Sexual/Romantic Content',
      description: 'Flag books with romance, dating, or sexual themes',
      color: 'text-pink-600',
      recommended: true
    },
    {
      key: 'flagControversialTopics',
      label: 'Controversial Topics',
      description: 'Flag books with potentially sensitive social or political content',
      color: 'text-orange-600',
      recommended: true
    },
    {
      key: 'flagViolence',
      label: 'Violence & Weapons',
      description: 'Flag books with violence, war, weapons, or fighting',
      color: 'text-red-600',
      recommended: false
    },
    {
      key: 'flagLanguage',
      label: 'Language Concerns',
      description: 'Flag books with profanity or inappropriate language',
      color: 'text-yellow-600',
      recommended: false
    },
    {
      key: 'enableDuplicateChecking',
      label: 'OPAC Duplicate Detection',
      description: 'Check LibraryWorld OPAC for existing copies before cataloging',
      color: 'text-blue-600',
      recommended: true
    },
    {
      key: 'strictFiltering',
      label: 'Strict Filtering Mode',
      description: 'Use stricter criteria to flag more content for review',
      color: 'text-purple-600',
      recommended: false
    }
  ];

  useEffect(() => {
    loadSettings();
  }, [currentUser]);

  useEffect(() => {
    if (settings) {
      setBarcodeInput(settings.lastBarcodeUsed.toString());
    }
  }, [settings]);

  const loadSettings = async () => {
    if (!currentUser) return;

    setLoading(true);
    try {
      const settingsRef = doc(db, 'users', currentUser.uid, 'settings', 'preferences');
      const settingsDoc = await getDoc(settingsRef);
      
      if (settingsDoc.exists()) {
        const savedSettings = settingsDoc.data();
        setSettings({
          ...savedSettings,
          lastBarcodeUsed: parseInt(userData?.lastBarcodeUsed || savedSettings.lastBarcodeUsed || 125863),
          schoolName: userData?.schoolName || savedSettings.schoolName || '',
          email: userData?.email || savedSettings.email || ''
        });
      } else {
        setSettings(null);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setSettings(null);
    }
    setLoading(false);
  };

  const saveSettings = async () => {
    if (!currentUser || !settings) return;

    setSaving(true);
    try {
      // Update email if changed
      if (settings.email !== currentUser.email) {
        await updateEmail(currentUser, settings.email);
      }

      // Save to settings document
      const settingsRef = doc(db, 'users', currentUser.uid, 'settings', 'preferences');
      await setDoc(settingsRef, {
        ...settings,
        lastUpdated: new Date()
      }, { merge: true });

      // Update main user document
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        schoolName: settings.schoolName,
        email: settings.email,
        lastBarcodeUsed: settings.lastBarcodeUsed
      });

      alert('Settings saved successfully! Changes will sync to ForgeOfOdin and LokisChamber automatically.');
    } catch (error) {
      console.error('Error saving settings:', error);
      if (error.code === 'auth/requires-recent-login') {
        alert('Please log out and log back in to change your email address.');
      } else {
        alert('Error saving settings: ' + error.message);
      }
    }
    setSaving(false);
  };

  const saveBarcode = async () => {
    if (!settings) return;
    
    const barcodeNum = parseInt(barcodeInput);
    if (!barcodeNum || barcodeNum < 1) {
      alert('Please enter a valid barcode number.');
      return;
    }

    setSavingBarcode(true);
    try {
      setSettings({...settings, lastBarcodeUsed: barcodeNum});
      
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, { lastBarcodeUsed: barcodeNum });
      
      alert('Barcode updated successfully!');
    } catch (error) {
      console.error('Error updating barcode:', error);
      alert('Error updating barcode: ' + error.message);
    }
    setSavingBarcode(false);
  };

  const changePassword = async () => {
    if (!passwordChange.currentPassword || !passwordChange.newPassword) {
      alert('Please fill in all password fields.');
      return;
    }

    if (passwordChange.newPassword !== passwordChange.confirmPassword) {
      alert('New passwords do not match.');
      return;
    }

    if (passwordChange.newPassword.length < 6) {
      alert('New password must be at least 6 characters.');
      return;
    }

    setPasswordChange(prev => ({...prev, loading: true}));

    try {
      // Reauthenticate user
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        passwordChange.currentPassword
      );
      await reauthenticateWithCredential(currentUser, credential);

      // Update password
      await updatePassword(currentUser, passwordChange.newPassword);

      alert('Password updated successfully!');
      setPasswordChange({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        showForm: false,
        loading: false
      });
    } catch (error) {
      console.error('Error changing password:', error);
      if (error.code === 'auth/wrong-password') {
        alert('Current password is incorrect.');
      } else {
        alert('Error changing password: ' + error.message);
      }
      setPasswordChange(prev => ({...prev, loading: false}));
    }
  };

  const testOPACConnection = async () => {
    if (!settings) return;
    
    setTestConnection(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      if (settings.opacUrl && settings.opacUsername) {
        alert('OPAC connection test successful! Connected to: ' + settings.opacUrl);
      } else {
        alert('OPAC connection test failed! Please check your URL and username.');
      }
    } catch (error) {
      alert('OPAC connection test failed! ' + error.message);
    }
    setTestConnection(false);
  };

  const resetFilteringToDefaults = () => {
    if (!settings || !window.confirm('Reset content filtering to recommended defaults?')) return;
    
    setSettings({
      ...settings,
      flagOverGrade8: true,
      flagSexualContent: true,
      flagControversialTopics: true,
      flagViolence: false,
      flagLanguage: false,
      enableDuplicateChecking: true,
      strictFiltering: false,
      enableContentFiltering: true
    });
  };

  const exportSettings = () => {
    if (!settings) return;
    
    const dataStr = JSON.stringify(settings, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'heimdl-settings.json';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  const tabs = [
    { id: 'filtering', label: 'Content Filtering', icon: '🛡️' },
    { id: 'barcodes', label: 'Barcode Management', icon: '🏷️' },
    { id: 'general', label: 'General', icon: '🏫' },
    { id: 'cataloging', label: 'Cataloging', icon: '📚' },
    { id: 'marc', label: 'MARC Config', icon: '📋' },
    { id: 'account', label: 'Account', icon: '👤' },
    { id: 'system', label: 'System', icon: '⚙️' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-xl text-slate-600">Loading settings...</div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="min-h-screen bg-slate-100">
        <header className="bg-gradient-to-r from-purple-900 to-indigo-900 text-white shadow-xl border-b border-purple-700/50">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
              <div className="flex items-center space-x-2 sm:space-x-4">
                <Logo size={40} showText={false} />
                <button onClick={() => navigate('/asgard')} className="text-lg sm:text-2xl font-bold hover:text-purple-200 tracking-wider">
                  HEIMDL
                </button>
                <span className="hidden sm:inline text-purple-300">|</span>
                <span className="hidden sm:inline text-purple-200">Yggdrasil</span>
              </div>
              <button onClick={handleLogout} className="text-sm sm:text-base bg-purple-700 hover:bg-purple-600 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg transition-colors">
                Leave Asgard
              </button>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-white rounded-xl shadow-xl p-8 border border-slate-200 text-center">
            <div className="text-6xl mb-6">🌳</div>
            <h1 className="text-3xl font-bold text-slate-800 mb-4">Yggdrasil Settings</h1>
            <p className="text-slate-600 mb-8">
              Your library system hasn't been configured yet. Complete the onboarding process to set up your preferences.
            </p>
            
            <div className="bg-yellow-100 rounded-lg p-6 border border-yellow-200 mb-8">
              <h3 className="font-bold text-yellow-800 mb-2">Setup Required</h3>
              <p className="text-yellow-700">
                Your HEIMDL system needs configuration with library information, barcode settings, and content filtering preferences.
              </p>
            </div>
            
            <div className="space-y-4">
              <button
                onClick={() => navigate('/onboarding')}
                className="w-full sm:w-auto bg-purple-600 text-white px-8 py-3 rounded-lg hover:bg-purple-700 font-medium"
              >
                Complete Setup Now
              </button>
              <br />
              <button
                onClick={() => navigate('/asgard')}
                className="w-full sm:w-auto bg-slate-500 text-white px-6 py-2 rounded-lg hover:bg-slate-600"
              >
                Return to Asgard
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-900 to-indigo-900 text-white shadow-xl border-b border-purple-700/50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <Logo size={40} showText={false} />
              <button onClick={() => navigate('/asgard')} className="text-lg sm:text-2xl font-bold hover:text-purple-200 tracking-wider">
                HEIMDL
              </button>
              <span className="hidden sm:inline text-purple-300">|</span>
              <span className="hidden sm:inline text-purple-200">Yggdrasil</span>
            </div>
            <div className="flex items-center space-x-3 sm:space-x-6">
              <div className="text-right">
                <p className="text-sm text-purple-300">Current Barcode</p>
                <p className="text-xl font-mono font-bold">{parseInt(settings.lastBarcodeUsed || 125863) + 1}</p>
              </div>
              <button onClick={handleLogout} className="text-sm sm:text-base bg-purple-700 hover:bg-purple-600 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg transition-colors">
                Leave Asgard
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="space-y-6">
          <div className="bg-slate-200 rounded-xl shadow-xl p-6 border border-slate-300">
            <h1 className="text-2xl font-bold mb-4 text-slate-800">🌳 Yggdrasil</h1>
            <p className="text-slate-600 mb-6">The world tree that connects all realms - configure your HEIMDL system</p>
            
            {/* Navigation Links */}
            <div className="mb-6 flex flex-wrap gap-2">
              <button onClick={() => navigate('/loki')} className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm">
                🐺 Content Review
              </button>
              <button onClick={() => navigate('/forge')} className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors text-sm">
                ⚒️ Forge of Odin
              </button>
              <button onClick={() => navigate('/allsight')} className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors text-sm">
                🔍 Browse Books
              </button>
            </div>
            
            {/* Save Actions */}
            <div className="flex justify-between items-center mb-6 bg-gradient-to-r from-emerald-100 to-blue-100 rounded-lg p-4 border border-emerald-200">
              <div>
                <h3 className="font-bold text-emerald-800">Configuration Management</h3>
                <p className="text-sm text-emerald-700">Settings automatically sync to ForgeOfOdin and LokisChamber</p>
              </div>
              <div className="space-x-2">
                <button onClick={exportSettings} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm">
                  📤 Export
                </button>
                <button
                  onClick={saveSettings}
                  disabled={saving}
                  className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:bg-slate-400 text-sm"
                >
                  {saving ? 'Saving...' : '💾 Save Settings'}
                </button>
              </div>
            </div>
          </div>

          {/* Settings Tabs */}
          <div className="bg-slate-200 rounded-xl shadow-xl border border-slate-300">
            {/* Tab Navigation */}
            <div className="border-b border-slate-300 bg-gradient-to-r from-slate-300 to-slate-250 rounded-t-xl">
              <div className="flex space-x-0 p-2">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      activeTab === tab.id
                        ? 'bg-purple-600 text-white'
                        : 'text-slate-700 hover:bg-slate-250'
                    }`}
                  >
                    {tab.icon} {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === 'filtering' && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-bold text-slate-800">Content Filtering Settings</h3>
                    <button
                      onClick={resetFilteringToDefaults}
                      className="bg-indigo-600 text-white px-3 py-1 rounded text-sm hover:bg-indigo-700"
                    >
                      Reset to Recommended
                    </button>
                  </div>
                  
                  <div className="bg-gradient-to-r from-purple-100 to-indigo-100 rounded-lg p-4 border border-purple-200 mb-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-bold text-purple-800">Master Content Filtering</h4>
                        <p className="text-sm text-purple-700">Enable AI-powered content analysis in ForgeOfOdin</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="masterFiltering"
                          checked={settings.enableContentFiltering}
                          onChange={(e) => setSettings({...settings, enableContentFiltering: e.target.checked})}
                          className="h-5 w-5 text-purple-600"
                        />
                        <label htmlFor="masterFiltering" className={`font-medium ${settings.enableContentFiltering ? 'text-purple-800' : 'text-purple-500'}`}>
                          {settings.enableContentFiltering ? 'Enabled ✅' : 'Disabled ❌'}
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {contentFilterOptions.map(option => (
                      <div key={option.key} className={`border rounded-lg p-4 ${
                        settings[option.key] ? 'border-green-300 bg-green-50' : 'border-slate-300 bg-white'
                      }`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <input
                                type="checkbox"
                                id={option.key}
                                checked={settings[option.key]}
                                onChange={(e) => setSettings({...settings, [option.key]: e.target.checked})}
                                className="h-4 w-4 text-green-600"
                                disabled={!settings.enableContentFiltering}
                              />
                              <label htmlFor={option.key} className={`font-medium ${option.color} ${!settings.enableContentFiltering ? 'opacity-50' : ''}`}>
                                {option.label}
                              </label>
                              {settings[option.key] && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  ✅ Active
                                </span>
                              )}
                            </div>
                            <p className={`text-sm text-slate-600 ${!settings.enableContentFiltering ? 'opacity-50' : ''}`}>
                              {option.description}
                            </p>
                            {option.recommended && (
                              <p className="text-xs text-blue-600 mt-1">Recommended for PreK-8</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-gradient-to-r from-yellow-100 to-orange-100 rounded-lg p-4 border border-yellow-200">
                    <h4 className="font-bold text-yellow-800 mb-2">How Content Filtering Works</h4>
                    <ul className="text-sm text-yellow-700 space-y-1">
                      <li>• AI analyzes book titles, subjects, descriptions, and audience data</li>
                      <li>• Flagged items are sent to Loki's Chamber for librarian review</li>
                      <li>• <strong>Flagged books receive temporary IDs</strong> - real barcodes assigned on approval</li>
                      <li>• Dismissed books don't consume barcodes, preventing gaps in your sequence</li>
                      <li>• You can approve (adds to batch) or dismiss (removes entirely)</li>
                      <li>• Unflagged items go directly to the cataloging batch with immediate barcodes</li>
                      <li>• Settings sync automatically to ForgeOfOdin</li>
                    </ul>
                  </div>
                </div>
              )}

              {activeTab === 'barcodes' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-bold text-slate-800">Barcode Management</h3>
                  
                  <div className="bg-gradient-to-r from-purple-100 to-blue-100 rounded-lg p-6 border border-purple-200 mb-6">
                    <div className="max-w-md">
                      <label className="block text-sm font-medium text-purple-700 mb-2">Last Used Barcode</label>
                      <div className="flex gap-2">
                        <input
                          type="number"
                          min="1"
                          className="flex-1 px-3 py-2 border border-purple-300 rounded-lg bg-white font-mono text-lg"
                          value={barcodeInput}
                          onChange={(e) => setBarcodeInput(e.target.value)}
                        />
                        <button
                          onClick={saveBarcode}
                          disabled={savingBarcode}
                          className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:bg-slate-400 whitespace-nowrap"
                        >
                          {savingBarcode ? 'Saving...' : '💾 Save'}
                        </button>
                      </div>
                      <p className="text-xs text-purple-600 mt-2">Next book will use: {parseInt(barcodeInput) + 1}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium text-slate-700 mb-3">Advanced Options</h4>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Barcode Prefix (optional)</label>
                          <input
                            type="text"
                            placeholder="e.g., HFCS"
                            maxLength="6"
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                            value={settings.barcodePrefix || ''}
                            onChange={(e) => setSettings({...settings, barcodePrefix: e.target.value.toUpperCase()})}
                          />
                          <p className="text-xs text-slate-500 mt-1">Will generate: {settings.barcodePrefix || ''}{parseInt(settings.lastBarcodeUsed) + 1}</p>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Skip Barcodes (comma-separated)</label>
                          <input
                            type="text"
                            placeholder="e.g., 125900,126000,126100"
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                            value={settings.skipBarcodes || ''}
                            onChange={(e) => setSettings({...settings, skipBarcodes: e.target.value})}
                          />
                          <p className="text-xs text-slate-500 mt-1">These numbers will be automatically skipped</p>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-medium text-slate-700 mb-3">Barcode History</h4>
                      <div className="bg-slate-50 rounded-lg p-4 border">
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-600">Starting barcode:</span>
                            <span className="font-mono">125864</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-600">Current barcode:</span>
                            <span className="font-mono font-bold">{parseInt(settings.lastBarcodeUsed)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-600">Next barcode:</span>
                            <span className="font-mono text-green-600">{parseInt(settings.lastBarcodeUsed) + 1}</span>
                          </div>
                          <div className="flex justify-between border-t pt-2">
                            <span className="text-slate-600">Books cataloged:</span>
                            <span className="font-bold">{Math.max(0, parseInt(settings.lastBarcodeUsed) - 125863)}</span>
                          </div>
                        </div>
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-xs text-blue-700 font-medium mb-1">📋 Smart Barcode Assignment</p>
                          <p className="text-xs text-slate-600">
                            Flagged books use temporary IDs. Real barcodes are only assigned when approved, 
                            ensuring no gaps in your sequence when books are dismissed.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'general' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-bold text-slate-800">School & Library Information</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">School Name *</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                        value={settings.schoolName || ''}
                        onChange={(e) => setSettings({...settings, schoolName: e.target.value})}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Library Name</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                        value={settings.libraryName || ''}
                        onChange={(e) => setSettings({...settings, libraryName: e.target.value})}
                        placeholder="Holy Family Library"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number</label>
                      <input
                        type="tel"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                        value={settings.phone || ''}
                        onChange={(e) => setSettings({...settings, phone: e.target.value})}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                        value={settings.address || ''}
                        onChange={(e) => setSettings({...settings, address: e.target.value})}
                        placeholder="123 School Lane, City, State 12345"
                      />
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-purple-100 to-indigo-100 rounded-lg p-4 border border-purple-200">
                    <h4 className="font-bold text-purple-800 mb-2">OPAC Connection</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-purple-700 mb-1">LibraryWorld URL</label>
                        <input
                          type="url"
                          className="w-full px-3 py-2 border border-purple-300 rounded-lg bg-white"
                          value={settings.opacUrl || ''}
                          onChange={(e) => setSettings({...settings, opacUrl: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-purple-700 mb-1">Username</label>
                        <input
                          type="text"
                          className="w-full px-3 py-2 border border-purple-300 rounded-lg bg-white"
                          value={settings.opacUsername || ''}
                          onChange={(e) => setSettings({...settings, opacUsername: e.target.value})}
                        />
                      </div>
                      <div className="flex items-end">
                        <button
                          onClick={testOPACConnection}
                          disabled={testConnection}
                          className="w-full bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:bg-slate-400"
                        >
                          {testConnection ? 'Testing...' : '🔗 Test Connection'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'cataloging' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-bold text-slate-800">Cataloging Defaults</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Default Location</label>
                      <select
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                        value={settings.defaultLocation || 'Fiction'}
                        onChange={(e) => setSettings({...settings, defaultLocation: e.target.value})}
                      >
                        {locations.map(location => (
                          <option key={location} value={location}>{location}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Price Markup ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                        value={settings.priceMarkup || 0}
                        onChange={(e) => setSettings({...settings, priceMarkup: parseFloat(e.target.value) || 0})}
                      />
                      <p className="text-xs text-slate-500 mt-1">Added to list price</p>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-blue-100 to-green-100 rounded-lg p-4 border border-blue-200">
                    <h4 className="font-bold text-blue-800 mb-2">Batch Processing</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-blue-700 mb-1">Recommended Batch Size</label>
                        <select
                          className="w-full px-3 py-2 border border-blue-300 rounded-lg bg-white"
                          value={settings.batchSize || 30}
                          onChange={(e) => setSettings({...settings, batchSize: parseInt(e.target.value)})}
                        >
                          <option value={15}>15 books (half barcode sheet)</option>
                          <option value={25}>25 books (half spine sheet)</option>
                          <option value={30}>30 books (full barcode sheet)</option>
                          <option value={50}>50 books (full spine sheet)</option>
                        </select>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="autoSave"
                          checked={settings.autoSave !== false}
                          onChange={(e) => setSettings({...settings, autoSave: e.target.checked})}
                          className="h-4 w-4 text-blue-600"
                        />
                        <label htmlFor="autoSave" className="text-sm text-blue-700">Auto-save progress</label>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'marc' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-bold text-slate-800">MARC Export Configuration</h3>
                  <p className="text-sm text-slate-600">
                    Configure which fields are included in your MARC exports. Toggle fields on/off based on your library system's requirements.
                  </p>
                  
                  {/* Required Fields */}
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <h4 className="font-semibold text-green-800 mb-3">Required Fields (Always Included)</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { tag: '001', label: 'Control Number (Barcode)' },
                        { tag: '245', label: 'Title Statement' },
                        { tag: '949', label: 'Local Holdings' }
                      ].map(field => (
                        <div key={field.tag} className="flex items-center space-x-2">
                          <input type="checkbox" checked disabled className="h-4 w-4 text-green-600" />
                          <span className="text-sm text-green-700">
                            <span className="font-mono font-bold">{field.tag}</span> - {field.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Bibliographic Fields */}
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <h4 className="font-semibold text-blue-800 mb-3">Bibliographic Fields</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {[
                        { key: 'isbn', tag: '020', label: 'ISBN' },
                        { key: 'author', tag: '100', label: 'Main Author' },
                        { key: 'title', tag: '245', label: 'Title/Subtitle' },
                        { key: 'edition', tag: '250', label: 'Edition Statement' },
                        { key: 'publication', tag: '264', label: 'Publication Info' },
                        { key: 'physicalDesc', tag: '300', label: 'Physical Description' },
                        { key: 'series', tag: '490', label: 'Series Statement' },
                        { key: 'generalNotes', tag: '500', label: 'General Notes' },
                        { key: 'summary', tag: '520', label: 'Summary/Description' },
                        { key: 'targetAudience', tag: '521', label: 'Target Audience' },
                        { key: 'subjects', tag: '650', label: 'Subject Headings' },
                        { key: 'genres', tag: '655', label: 'Genre/Form' },
                        { key: 'localSubjects', tag: '690', label: 'Local Subject Headings' },
                        { key: 'coAuthors', tag: '700', label: 'Additional Authors' }
                      ].map(field => (
                        <label key={field.key} className="flex items-center space-x-2 cursor-pointer hover:bg-blue-100 p-1 rounded">
                          <input
                            type="checkbox"
                            checked={settings.marcConfig?.fields?.[field.key] !== false}
                            onChange={(e) => setSettings({
                              ...settings,
                              marcConfig: {
                                ...settings.marcConfig,
                                fields: {
                                  ...settings.marcConfig?.fields,
                                  [field.key]: e.target.checked
                                }
                              }
                            })}
                            className="h-4 w-4 text-blue-600"
                          />
                          <span className="text-sm text-blue-700">
                            <span className="font-mono font-bold">{field.tag}</span> - {field.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  {/* Enhanced Data Fields */}
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <h4 className="font-semibold text-purple-800 mb-3">Enhanced Data (From Library Sources)</h4>
                    <p className="text-xs text-purple-600 mb-3">These fields are populated from Library of Congress, Open Library, and Google Books when available.</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {[
                        { key: 'deweyDecimal', label: 'Dewey Decimal Classification' },
                        { key: 'lccn', label: 'Library of Congress Control Number' },
                        { key: 'lexileLevel', label: 'Lexile Level' },
                        { key: 'readingLevel', label: 'Reading Level' },
                        { key: 'ageRange', label: 'Age Range' },
                        { key: 'awards', label: 'Awards' },
                        { key: 'forFansOf', label: 'For Fans Of' }
                      ].map(field => (
                        <label key={field.key} className="flex items-center space-x-2 cursor-pointer hover:bg-purple-100 p-1 rounded">
                          <input
                            type="checkbox"
                            checked={settings.marcConfig?.enhanced?.[field.key] !== false}
                            onChange={(e) => setSettings({
                              ...settings,
                              marcConfig: {
                                ...settings.marcConfig,
                                enhanced: {
                                  ...settings.marcConfig?.enhanced,
                                  [field.key]: e.target.checked
                                }
                              }
                            })}
                            className="h-4 w-4 text-purple-600"
                          />
                          <span className="text-sm text-purple-700">{field.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  {/* Notes Configuration */}
                  <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                    <h4 className="font-semibold text-amber-800 mb-3">Notes Configuration</h4>
                    <p className="text-xs text-amber-600 mb-3">Control what information is included in MARC notes fields.</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {[
                        { key: 'includeAwardsInCopy', label: 'Include Awards in Copy Notes' },
                        { key: 'includeReadingLevels', label: 'Include Reading Levels' },
                        { key: 'includeForFansOf', label: 'Include "For Fans Of"' },
                        { key: 'includeDonationNotes', label: 'Include Donation Notes' },
                        { key: 'includePriceInfo', label: 'Include Price Information' },
                        { key: 'includeDataSources', label: 'Include Data Sources' }
                      ].map(field => (
                        <label key={field.key} className="flex items-center space-x-2 cursor-pointer hover:bg-amber-100 p-1 rounded">
                          <input
                            type="checkbox"
                            checked={settings.marcConfig?.notes?.[field.key] !== false}
                            onChange={(e) => setSettings({
                              ...settings,
                              marcConfig: {
                                ...settings.marcConfig,
                                notes: {
                                  ...settings.marcConfig?.notes,
                                  [field.key]: e.target.checked
                                }
                              }
                            })}
                            className="h-4 w-4 text-amber-600"
                          />
                          <span className="text-sm text-amber-700">{field.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  {/* Subject Headings Limit */}
                  <div className="bg-slate-100 rounded-lg p-4 border border-slate-300">
                    <h4 className="font-semibold text-slate-800 mb-3">Subject Headings</h4>
                    <div className="flex items-center space-x-4">
                      <label className="text-sm text-slate-700">Maximum subject headings per record:</label>
                      <select
                        value={settings.marcConfig?.maxSubjects || 5}
                        onChange={(e) => setSettings({
                          ...settings,
                          marcConfig: {
                            ...settings.marcConfig,
                            maxSubjects: parseInt(e.target.value)
                          }
                        })}
                        className="px-3 py-2 border border-slate-300 rounded-lg bg-white"
                      >
                        <option value={3}>3 subjects</option>
                        <option value={5}>5 subjects</option>
                        <option value={8}>8 subjects</option>
                        <option value={10}>10 subjects</option>
                        <option value={15}>15 subjects (max)</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Quick Presets */}
                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-200">
                    <h4 className="font-semibold text-indigo-800 mb-3">Quick Presets</h4>
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={() => setSettings({
                          ...settings,
                          marcConfig: {
                            fields: { isbn: true, author: true, title: true, publication: true, physicalDesc: true, subjects: true },
                            enhanced: { deweyDecimal: true },
                            notes: { includeAwardsInCopy: true },
                            maxSubjects: 5
                          }
                        })}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm"
                      >
                        Standard (Recommended)
                      </button>
                      <button
                        onClick={() => setSettings({
                          ...settings,
                          marcConfig: {
                            fields: { isbn: true, author: true, title: true, publication: true },
                            enhanced: {},
                            notes: {},
                            maxSubjects: 3
                          }
                        })}
                        className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 text-sm"
                      >
                        Minimal
                      </button>
                      <button
                        onClick={() => setSettings({
                          ...settings,
                          marcConfig: {
                            fields: { isbn: true, author: true, title: true, edition: true, publication: true, physicalDesc: true, series: true, generalNotes: true, summary: true, targetAudience: true, subjects: true, genres: true, localSubjects: true, coAuthors: true },
                            enhanced: { deweyDecimal: true, lccn: true, lexileLevel: true, readingLevel: true, ageRange: true, awards: true, forFansOf: true },
                            notes: { includeAwardsInCopy: true, includeReadingLevels: true, includeForFansOf: true, includeDonationNotes: true },
                            maxSubjects: 10
                          }
                        })}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
                      >
                        Full (All Fields)
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'account' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-bold text-slate-800">Account Settings</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium text-slate-700 mb-3">Contact Information</h4>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Email Address</label>
                          <input
                            type="email"
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                            value={settings.email || ''}
                            onChange={(e) => setSettings({...settings, email: e.target.value})}
                          />
                          <p className="text-xs text-slate-500 mt-1">Changes require re-authentication</p>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-medium text-slate-700 mb-3">Password</h4>
                      {!passwordChange.showForm ? (
                        <button
                          onClick={() => setPasswordChange(prev => ({...prev, showForm: true}))}
                          className="bg-slate-600 text-white px-4 py-2 rounded-lg hover:bg-slate-700"
                        >
                          Change Password
                        </button>
                      ) : (
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Current Password</label>
                            <input
                              type="password"
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                              value={passwordChange.currentPassword}
                              onChange={(e) => setPasswordChange(prev => ({...prev, currentPassword: e.target.value}))}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">New Password</label>
                            <input
                              type="password"
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                              value={passwordChange.newPassword}
                              onChange={(e) => setPasswordChange(prev => ({...prev, newPassword: e.target.value}))}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Confirm New Password</label>
                            <input
                              type="password"
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                              value={passwordChange.confirmPassword}
                              onChange={(e) => setPasswordChange(prev => ({...prev, confirmPassword: e.target.value}))}
                            />
                          </div>
                          <div className="flex space-x-3">
                            <button
                              onClick={changePassword}
                              disabled={passwordChange.loading}
                              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:bg-slate-400"
                            >
                              {passwordChange.loading ? 'Updating...' : 'Update Password'}
                            </button>
                            <button
                              onClick={() => setPasswordChange({
                                currentPassword: '',
                                newPassword: '',
                                confirmPassword: '',
                                showForm: false,
                                loading: false
                              })}
                              className="bg-slate-500 text-white px-4 py-2 rounded-lg hover:bg-slate-600"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-blue-100 to-indigo-100 rounded-lg p-4 border border-blue-200">
                    <h4 className="font-bold text-blue-800 mb-2">Subscription Information</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-blue-600 font-medium">Plan</p>
                        <p className="text-blue-700">{userData?.subscription || 'Unknown'}</p>
                      </div>
                      <div>
                        <p className="text-blue-600 font-medium">Status</p>
                        <p className="text-blue-700">Active</p>
                      </div>
                      <div>
                        <p className="text-blue-600 font-medium">Member Since</p>
                        <p className="text-blue-700">{userData?.createdAt?.toLocaleDateString() || 'Unknown'}</p>
                      </div>
                      <div>
                        <p className="text-blue-600 font-medium">Books Cataloged</p>
                        <p className="text-blue-700">{Math.max(0, parseInt(settings.lastBarcodeUsed || 125863) - 125863)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'system' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-bold text-slate-800">System Preferences</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium text-slate-700 mb-3">Interface</h4>
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="notifications"
                            checked={settings.enableNotifications !== false}
                            onChange={(e) => setSettings({...settings, enableNotifications: e.target.checked})}
                            className="h-4 w-4 text-purple-600"
                          />
                          <label htmlFor="notifications" className="text-sm text-slate-700">Enable browser notifications</label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-purple-100 to-blue-100 rounded-lg p-4 border border-purple-200">
                    <h4 className="font-bold text-purple-800 mb-2">System Information</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-purple-600 font-medium">Version</p>
                        <p className="text-purple-700">HEIMDL v1.0</p>
                      </div>
                      <div>
                        <p className="text-purple-600 font-medium">Last Barcode</p>
                        <p className="text-purple-700 font-mono">{parseInt(settings.lastBarcodeUsed)}</p>
                      </div>
                      <div>
                        <p className="text-purple-600 font-medium">Books Cataloged</p>
                        <p className="text-purple-700">{Math.max(0, parseInt(settings.lastBarcodeUsed || 125863) - 125863)}</p>
                      </div>
                      <div>
                        <p className="text-purple-600 font-medium">Content Filtering</p>
                        <p className="text-purple-700">{settings.enableContentFiltering ? 'Active ✅' : 'Disabled ❌'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default Yggdrasil;